import { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sentry, sentryEnabled } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  /** Short label shown in the fallback (e.g., "Appointments tab"). */
  label?: string;
  /** Optional custom fallback. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Per-section error boundary. Use to wrap dashboard tabs / large panels
 * so a single component crash doesn't blank the entire page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (sentryEnabled) {
      Sentry.withScope((scope) => {
        scope.setTag("boundary", this.props.label || "unknown");
        scope.setExtra("componentStack", info.componentStack);
        Sentry.captureException(error);
      });
    }
    // Always log so it appears in the user's preview console
     
    console.error(`[ErrorBoundary:${this.props.label || "unknown"}]`, error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="p-6 flex flex-col items-start gap-3">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" aria-hidden="true" />
            <h3 className="font-medium">
              {this.props.label ? `${this.props.label} couldn't load` : "Something went wrong"}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            The rest of the page is still working. You can try again or refresh.
          </p>
          {this.state.error?.message && (
            <p className="text-xs text-muted-foreground/70 font-mono break-all">
              {this.state.error.message}
            </p>
          )}
          <Button size="sm" variant="outline" onClick={this.reset}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
}

export default ErrorBoundary;
